/**
 * Helper: Product
 * -----------------------------------------------------------------------------
 * Common product and variant logic.
 * - Add in alphabetical order.
 *
 */

import Cookies from 'js-cookie'

/**
 * Handle adding the product properties if a specific tag is present
 * so that it can be seen at checkout within the line item.
 */
export function addTagProperties(product, properties) {
  const tags = getTagProperties()

  tags.forEach((tag) => {
    if (isTagAvailable(product, tag.productTag)) {
      properties[tag.propertyName] = tag.themeString
    }
  })
}

/**
 * Get the variant option where the name matches 'color'.
 * @param {Object} product - The formatted Graphql product response.
 * @returns {Object|undefined} - The product option object with the name
 * 'color'.
 */
export function getColors(product) {
  return product.options.find(option =>
    option.name.match(/color/ig),
  )
}

/**
 * Get the variant options formatted with pipe separators.
 * @param {Object} variant
 * @returns {string}
 */
export function getFormattedVariantTitle(variant) {
  return variant.selectedOptions.map(
    option => option.value,
  ).join(' | ')
}

/**
 * Reusable line item properties to keep ATC consistent.
 * @param {Object} product - Canvas formatted product.
 * @param {Object} variant - Canvas formatted variant that is being added to
 * cart.
 * @returns {{Sale: string, Text: string, Discounts: string, Shipping: string}}
 */
export function getLineItemProperties(product = null, variant = null) {
  const properties = {
    Text: '',
    Sale: '',
    Discounts: '',
    Shipping: '',
    Preorder: getPreorderText(product, variant),
    NonStandardShipping: getNonStandardShippingText(product, variant),
  }

  if (product?.tags) {
    addTagProperties(product, properties)
  }

  const schoolCode = Cookies.get('sc')
  if(schoolCode){

    properties['_SchoolCode'] = schoolCode
  }

  return properties
}

/**
 * Get metafield value.
 * @param {Object} metafields - Object of metafields
 * @param {String} namespace - Metafield namespace
 * @param {String} key - Metafield key
 * @return {Boolean|Object}
 */
export function getMetafield(metafields = {}, namespace = '', key = '') {
  metafields = JSON.parse(JSON.stringify(metafields))

  if (!metafields || !metafields[namespace]) {
    return false
  } else if (metafields[namespace][key]) {
    return metafields[namespace][key]
  } else {
    return false
  }
}

/**
 * Get all variant option values where the name does not match 'color'.
 * @param {Object} product - The formatted GraphQL product response
 * @returns {Object|undefined} - The product option with a non color name,
 * or undefined if a product has only default variant.
 */
export function getNonColors(product) {
  if (product.hasOnlyDefaultVariant) {
    return undefined
  }
  return product.options.find(option =>
    !option.name.match(/color/ig),
  )
}

/**
 * Get the preorder messaging.
 * @param product - Canvas formatted product.
 * @param variant - Canvas formatted variant.
 * @returns {string}
 */
export function getPreorderText(product, variant) {
  if (!isPreorder(product, variant)) {
    return ''
  }
  const namespace = 'global'
  const key = 'preorder_date'
  let preorder
  if (variant) {
    preorder = getMetafield(
      product?.isBoost ? product.metafields : variant.metafields,
      product?.isBoost ? variant.id : namespace,
      key,
    )
  }
  if (!preorder || preorder.value === 'null') {
    preorder = getMetafield(product?.metafields, namespace, key)
  }
  if (preorder && preorder.value !== 'null') {
    return `${preorder.value}`
  }
  return ''
}

/**
 * Get the non-standard shipping messaging.
 * @param product - Canvas formatted product.
 * @param variant - Canvas formatted variant.
 * @returns {string}
 */
export function getNonStandardShippingText(product, variant) {
  const namespace = 'global'
  const key = 'non_standard_ship_by_message'
  let nonStandardShipping

  if (variant) {
    nonStandardShipping = getMetafield(
      product?.isBoost ? product.metafields : variant.metafields,
      product?.isBoost ? variant.id : namespace,
      key,
    )
  }

  if (!nonStandardShipping || nonStandardShipping.value === 'null') {
    nonStandardShipping = getMetafield(product?.metafields, namespace, key)
  }

  if (nonStandardShipping && nonStandardShipping.value !== 'null') {
    return `${nonStandardShipping.value}`
  }

  return ''
}

/**
 * Get an object of images associated with swatches. Each color value is a
 * unique key.
 * @param {Object} product - The formatted GraphQL product response.
 * @param {String[]} colors - An array of color values.
 * @returns {Object} - Returns an object with [color value]: [image Object]
 * pairs.
 */
export function getSwatchImages(product, colors) {
  const images = {}

  colors.forEach(color => {
    const image = product.images.find(image => {
      const alt = image.alt?.toLowerCase() || ''
      return alt.includes(color.toLowerCase()) && alt.includes('swatch')
    })

    if (image) {
      images[color] = image
    }
  })

  return images
}

/**
 * Get tag properties
 * @param {String[]} omittedTags - A list of tag names to omit.
 * @returns {({propertyName: string, productTag: string, themeString: string}|{propertyName: string, productTag: string, themeString: string}|{propertyName: string, productTag: string, themeString: string})[]}
 */
export function getTagProperties(omittedTags = []) {
  return [
    {
      'productTag': 'final-sale',
      'propertyName': 'Sale',
      'themeString': cnvs.strings.product.form.final_sale,
    },
    {
      'productTag': 'discount:not-eligible',
      'propertyName': 'Discounts',
      'themeString': cnvs.strings.product.form.discount_not_eligible,
    },
    {
      'productTag': 'shipping_surcharge:oversize',
      'propertyName': 'Shipping',
      'themeString': cnvs.strings.product.form.oversized_shipping,
    },
  ].filter(property => omittedTags.indexOf(property.productTag) === -1)
}

/**
 * Get a variant by ID.
 * @param {Object} product - The formatted GraphQL product response.
 * @param {Number} variantId - The variant ID to look for.
 * @returns {Object|undefined}
 */
export function getVariantById(product, variantId) {
  return product.variants.find(variant => variant.id === Number(variantId))
}

/**
 * Get a variant based on selected options.
 * @param {Boolean} availableOnly - Available variants only?
 * @param {Object} product - The formatted GraphQL product response.
 * @param {Object[]} selectedOptions - An array of selected options.
 * Optional if the product has only default variant.
 * @param {String} selectedOptions[].name - The option name e.g 'color'.
 * @param {String} selectedOptions[].value - The option value e.g. 'pink'.
 * @returns {Object|undefined} - The product variant if found.
 */
export function getVariantByOptions(product, selectedOptions, availableOnly = false) {
  const strOptions = JSON.stringify(selectedOptions)

  if (product.hasOnlyDefaultVariant) {
    return product.variants[0]
  }

  return product.variants.find(variant => {
    const match = JSON.stringify(variant.selectedOptions) === strOptions
    return availableOnly ? match && variant.available : match
  })
}

/**
 * Get a variant based a title generated from selected options.
 * @param {Object} product - The formatted GraphQL product response.
 * @param {Object[]} selectedOptions - An array of selected options.
 * Optional if the product has only default variant.
 * @param {String} title - The title to look for if selected options is omitted.
 * @returns {Object|undefined|Boolean}
 */
export function getVariantByTitle(product, selectedOptions, title) {
  let formattedTitle = ''
  if (product.hasOnlyDefaultVariant) {
    return product.variants[0]
  }
  if (Array.isArray(selectedOptions)) {
    formattedTitle = selectedOptions.map(option => option.value).join(' / ')
  } else if (typeof title === 'string') {
    formattedTitle = title
  } else {
    return false
  }
  formattedTitle = formattedTitle.toLowerCase()
  return product.variants.find(
    variant => formattedTitle === variant.title.toLowerCase(),
  )
}

/**
 * Get a variant image
 * @param {Object} product - The formatted GraphQL product response.
 * @param {Object} variant - The variant to look for.
 * @returns {Object} - The variant image, or, if it doesn't exist, the
 * product's featured image.
 */
export function getVariantImage(product, variant) {
  if (!product) {
    return false
  }
  if (!variant) {
    return product.featuredImage
  }
  const defaultImage = product.featuredImage
  const variantImage = product.images.find(
    image => image.variant === variant?.id || image.id === variant?.image,
  )
  const color = variant?.selectedOptions.find(
    option => option.name.match(/color/gi),
  )
  if (color && (!variantImage || color.value.toLowerCase() !== variantImage?.alt?.toLowerCase())) {
    const colorImages = getVariantImagesByColor(product, color.value)
    if (colorImages.length) {
      return colorImages[0]
    }
  }

  return variantImage || defaultImage
}

/**
 * Get an image by color using alt text.
 * @param {Object} product - The formatted GraphQL product response.
 * @param {String} color - The color to match.
 * @returns {undefined|Object}
 */
export function getVariantImagesByColor(product, color) {
  return product.images.filter(image => image.alt?.toLowerCase() === color.toLowerCase())
}

/**
 * Check if product is a pre-order product.
 * @param product - Canvas formatted product.
 * @param variant - Canvas formatted variant.
 * @returns {Boolean}
 */
 export function isPreorder(product, variant) {
  const hasTag = product && (product?.tags?.includes('preorder2024') || product?.tags?.includes('purple-dot-live-waitlist'))
  if (!hasTag || !variant) {
    return false
  }
  let variantMetafield = getMetafield(
    variant.metafields,
    'global',
    'preorder_date',
  )
  return Object.keys(variantMetafield).length > 0
}

/**
 * Check if product tag is available.
 * @param {Object} product - The formatted GraphQL product response.
 * @param {String} tag - The tag to look for.
 * @returns {boolean}
 */
export function isTagAvailable(product, tag) {
  return product?.tags?.indexOf(tag) > -1
}

/**
 * Check if there is an available variant with the given option value.
 * @param {Object} product - The formatted GraphQL product response.
 * @param {String|String[]} optionValue - The option value or an array of
 * values.
 * @returns {boolean}
 */
export function isVariantOptionAvailable(product, optionValue) {
  if (!product) {
    return true
  }

  let options = []

  if (typeof optionValue === 'string') {
    options.push(optionValue)
  }
  else if (Array.isArray(optionValue)) {
    options = optionValue.filter(Boolean)
  }

  if (product.isBoost) {
    const boostOptions = options.join('/')
      .split('/')
      .map(option => option.trim().toLowerCase())

    return product.variants.filter(variant => {
      const selectedOptions = variant.title.split('/').map(
        option => option.trim().toLowerCase(),
      )
      return boostOptions.every(
        option => selectedOptions.indexOf(option) > -1,
      ) && variant.available
    }).length > 0
  }

  return product.variants.filter(variant => {
    const valuesAreInSelectedOptions = options.every(value => {
      return variant.selectedOptions.some(option =>
        option.value.toLowerCase() === value.toLowerCase(),
      )
    })

    return valuesAreInSelectedOptions && variant.available
  }).length > 0
}
